import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  BarChart3, Users, MapPin, Clock, Calendar, Download,
  TrendingUp, TrendingDown, Target, CheckCircle, Store
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useBusiness } from '../../contexts/BusinessContext';
import { toast } from 'sonner';
import api from '../../services/api';
import { getApiErrorMessage } from '../../utils/apiErrors';

const TrackingReportsPage = () => {
  const { activeBusiness } = useBusiness();
  const { businessId } = useParams();
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('THIS_WEEK');
  const [selectedStaff, setSelectedStaff] = useState('');
  const [staffList, setStaffList] = useState([]);

  useEffect(() => {
    fetchData();
  }, [selectedPeriod, selectedStaff]);

  const fetchData = async () => {
    setLoading(true);
    await Promise.all([
      fetchReportData(),
      fetchStaffList()
    ]);
    setLoading(false);
  };

  const fetchReportData = async () => {
    try {
      const params = { period: selectedPeriod };
      if (selectedStaff) params.staff_id = selectedStaff;
      const { data } = await api.get('/part3/tracking/reports', { params });
      setReportData(data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load report data'));
      setReportData(null);
    }
  };

  const fetchStaffList = async () => {
    try {
      const { data } = await api.get('/part3/staff');
      setStaffList(data || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load staff list'));
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getInitialColor = (name) => {
    const colors = [
      'bg-blue-100 text-blue-700',
      'bg-emerald-100 text-emerald-700',
      'bg-purple-100 text-purple-700',
      'bg-amber-100 text-amber-700',
      'bg-rose-100 text-rose-700',
      'bg-cyan-100 text-cyan-700',
    ];
    const index = (name?.charCodeAt(0) || 0) % colors.length;
    return colors[index];
  };

  const periods = [
    { value: 'TODAY', label: 'Today' },
    { value: 'THIS_WEEK', label: 'This Week' },
    { value: 'THIS_MONTH', label: 'This Month' },
    { value: 'LAST_MONTH', label: 'Last Month' },
  ];

  const maxVisits = Math.max(...(reportData?.daily_visits?.map(d => d.visits) || [0]));

  return (
    <div className="h-full flex flex-col bg-[#F7F8FA]">
      {/* Page Header */}
      <div className="flex-shrink-0 bg-[#F7F8FA] px-6 pt-5 pb-4">
        <div className="max-w-[1600px] mx-auto">
          {/* Title Row */}
          <div className="flex items-start justify-between mb-5">
            <div>
              <h1 className="text-xl font-semibold text-slate-800 tracking-tight">Tracking Reports</h1>
              <p className="text-[13px] text-slate-500 mt-0.5 font-normal">Analyze field staff performance and coverage</p>
            </div>
            <div className="flex items-center gap-2">
              <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                <SelectTrigger className="w-40 bg-white">
                  <Calendar className="w-4 h-4 mr-2 text-slate-400" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {periods.map(period => (
                    <SelectItem key={period.value} value={period.value}>{period.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={selectedStaff || "all"} onValueChange={(val) => setSelectedStaff(val === "all" ? "" : val)}>
                <SelectTrigger className="w-48 bg-white">
                  <SelectValue placeholder="All Staff" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Staff</SelectItem>
                  {staffList.map(staff => (
                    <SelectItem key={staff.id} value={staff.id}>{staff.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <button
                onClick={() => toast.info('Export feature coming soon')}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition-all text-[13px] font-medium shadow-sm"
              >
                <Download className="w-4 h-4" strokeWidth={2} />
                Export
              </button>
            </div>
          </div>

          {/* Stats Pills */}
          <div className="flex items-center gap-3 mb-5">
            <div className="inline-flex items-center gap-2.5 px-3.5 py-2 bg-white rounded-lg shadow-sm">
              <div className="w-7 h-7 rounded-md bg-blue-50 flex items-center justify-center">
                <MapPin className="w-3.5 h-3.5 text-blue-600" />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Total Visits</p>
                <div className="flex items-center gap-1.5">
                  <p className="text-[14px] font-semibold text-slate-700">{reportData?.summary?.total_visits || 0}</p>
                  {reportData?.summary?.visits_change !== 0 && (
                    <span className={`flex items-center text-[10px] font-medium ${reportData?.summary?.visits_change > 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                      {reportData?.summary?.visits_change > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                      {Math.abs(reportData?.summary?.visits_change || 0)}%
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            <div className="inline-flex items-center gap-2.5 px-3.5 py-2 bg-white rounded-lg shadow-sm">
              <div className="w-7 h-7 rounded-md bg-emerald-50 flex items-center justify-center">
                <TrendingUp className="w-3.5 h-3.5 text-emerald-600" />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Distance</p>
                <div className="flex items-center gap-1.5">
                  <p className="text-[14px] font-semibold text-slate-700">{reportData?.summary?.total_distance?.toFixed(1) || 0} km</p>
                  {reportData?.summary?.distance_change !== 0 && (
                    <span className={`flex items-center text-[10px] font-medium ${reportData?.summary?.distance_change > 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                      {reportData?.summary?.distance_change > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                      {Math.abs(reportData?.summary?.distance_change || 0)}%
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            <div className="inline-flex items-center gap-2.5 px-3.5 py-2 bg-white rounded-lg shadow-sm">
              <div className="w-7 h-7 rounded-md bg-purple-50 flex items-center justify-center">
                <Clock className="w-3.5 h-3.5 text-purple-600" />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Avg Duration</p>
                <div className="flex items-center gap-1.5">
                  <p className="text-[14px] font-semibold text-slate-700">{reportData?.summary?.avg_visit_duration || 0} min</p>
                  {reportData?.summary?.duration_change !== 0 && (
                    <span className={`flex items-center text-[10px] font-medium ${reportData?.summary?.duration_change > 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                      {reportData?.summary?.duration_change > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                      {Math.abs(reportData?.summary?.duration_change || 0)}%
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="inline-flex items-center gap-2.5 px-3.5 py-2 bg-white rounded-lg shadow-sm">
              <div className="w-7 h-7 rounded-md bg-amber-50 flex items-center justify-center">
                <Target className="w-3.5 h-3.5 text-amber-600" />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Coverage</p>
                <div className="flex items-center gap-1.5">
                  <p className="text-[14px] font-semibold text-slate-700">{reportData?.summary?.coverage_rate || 0}%</p>
                  {reportData?.summary?.coverage_change !== 0 && (
                    <span className={`flex items-center text-[10px] font-medium ${reportData?.summary?.coverage_change > 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                      {reportData?.summary?.coverage_change > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                      {Math.abs(reportData?.summary?.coverage_change || 0)}%
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-auto px-6 pb-6">
        <div className="max-w-[1600px] mx-auto">
          {loading ? (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 flex items-center justify-center py-20">
              <div className="w-10 h-10 border-3 border-slate-200 border-t-teal-600 rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Daily Visits Chart */}
              <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-slate-200 p-5">
                <h3 className="text-[14px] font-semibold text-slate-800 mb-4">Daily Visits</h3>
                <div className="h-64 flex items-end gap-4">
                  {reportData?.daily_visits?.map((day, index) => (
                    <div key={index} className="flex-1 flex flex-col items-center gap-2">
                      <div className="w-full flex flex-col items-center">
                        <span className="text-[11px] font-medium text-slate-600 mb-1">{day.visits}</span>
                        <div 
                          className="w-full bg-teal-500 rounded-t-md transition-all duration-300"
                          style={{ 
                            height: maxVisits > 0 ? `${(day.visits / maxVisits) * 180}px` : '4px',
                            minHeight: '4px'
                          }}
                        ></div>
                      </div>
                      <span className="text-[11px] text-slate-500">{day.day}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top Performers */}
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
                <h3 className="text-[14px] font-semibold text-slate-800 mb-4">Top Performers</h3>
                <div className="space-y-4">
                  {reportData?.staff_performance?.slice(0, 5).map((staff, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <span className="text-[12px] font-semibold text-slate-400 w-5">{index + 1}</span>
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center text-[12px] font-semibold ${getInitialColor(staff.name)}`}>
                        {getInitials(staff.name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-medium text-slate-800 truncate">{staff.name}</p>
                        <p className="text-[11px] text-slate-500">{staff.visits} visits • {staff.distance?.toFixed(1)} km</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[13px] font-semibold text-teal-600">{staff.coverage}%</p>
                        <p className="text-[10px] text-slate-400">coverage</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Staff Performance Table */}
              <div className="lg:col-span-3 bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-100">
                  <h3 className="text-[14px] font-semibold text-slate-800">Staff Performance Details</h3>
                </div>
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="text-left px-5 py-3 text-[11px] font-semibold text-teal-700 uppercase tracking-wider">Staff Member</th>
                      <th className="text-center px-5 py-3 text-[11px] font-semibold text-teal-700 uppercase tracking-wider">Visits</th>
                      <th className="text-center px-5 py-3 text-[11px] font-semibold text-teal-700 uppercase tracking-wider">Distance</th>
                      <th className="text-center px-5 py-3 text-[11px] font-semibold text-teal-700 uppercase tracking-wider">Avg Duration</th>
                      <th className="text-center px-5 py-3 text-[11px] font-semibold text-teal-700 uppercase tracking-wider">Coverage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData?.staff_performance?.map((staff, index) => (
                      <tr key={index} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-3">
                            <div className={`w-9 h-9 rounded-full flex items-center justify-center text-[12px] font-semibold ${getInitialColor(staff.name)}`}>
                              {getInitials(staff.name)}
                            </div>
                            <span className="text-[13px] font-medium text-slate-800">{staff.name}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3.5 text-center">
                          <span className="text-[13px] font-semibold text-slate-700">{staff.visits}</span>
                        </td>
                        <td className="px-5 py-3.5 text-center">
                          <span className="text-[13px] text-slate-700">{staff.distance?.toFixed(1)} km</span>
                        </td>
                        <td className="px-5 py-3.5 text-center">
                          <span className="text-[13px] text-slate-700">{staff.avg_duration} min</span>
                        </td>
                        <td className="px-5 py-3.5 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <div className="w-20 h-2 bg-slate-100 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-teal-500 rounded-full"
                                style={{ width: `${staff.coverage}%` }}
                              ></div>
                            </div>
                            <span className="text-[12px] font-semibold text-slate-700">{staff.coverage}%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrackingReportsPage;
